import { createStackNavigator, StackNavigationProp } from '@react-navigation/stack';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { MenuProvider } from 'react-native-popup-menu';

import ChangeLogs from './pages/ChangeLogs';
import ContactUs from './pages/ContactUs';
import FrequentlyAskedQuestions from './pages/FrequentlyAskedQuestions';
import PublicFrequentlyAskedQuestions from './pages/PublicFrequentlyAskedQuestions';
import Login from './pages/Login';
import Menu from './pages/Menu';
import ResetPassword from './pages/ResetPassword';
import TermsOfUse from './pages/TermsOfUse';
import { i18nKeys, translate } from './services/translate';

import {
    faClipboardList,
    faMap,
    faBuildingShield,
    faCar,
    faFolderOpen,
    faShield,
    faTag,
    faUser,
    faDownload,
    faMagnifyingGlass,
    faBell,
    faTriangleExclamation,
    faUserLock,
    faClipboard,
    faLock,
    faKey,
    faCalendar,
    faNoteSticky,
    faTicket,
    faVenus,
    faSitemap,
    faCalculator,
    faPlug,
    faScrewdriverWrench,
    faFileContract,
    faCloudArrowUp,
    faCloudArrowDown,
    faServer,
    faBan,
    faFilePen,
    faHexagonNodesBolt,
    faCodeCommit,
    faPhone,
    faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faCameraCctv,
    IconDefinition as ProIconDefinition,
    faSearch,
    faIdBadge,
    faFileSignature,
    faMemoCircleInfo,
    faMemoCircleCheck,
    faUserPolice,
    faCameraSlash,
    faCarBus,
    faClipboardListCheck,
    faClock,
    faHandHoldingBox,
    faChartPieSimple,
} from '@fortawesome/pro-solid-svg-icons';


// cameras
import AlertGroups from './pages/cameras/AlertGroups/AlertGroups';
import CameraPlayer from './pages/cameras/CameraPlayer/CameraPlayer';
import CameraDetails from './pages/cameras/Cameras/CameraDetails';
import CamerasList from './pages/cameras/Cameras/CamerasList';
import CamerasMap from './pages/cameras/CamerasMap/CamerasMap';
import CameraTags from './pages/cameras/CameraTags/CameraTags';
import Downloads from './pages/cameras/Downloads/Downloads';
import DownloadTags from './pages/cameras/DownloadTags/DownloadTags';
import Mosaic from './pages/cameras/Mosaic/Mosaic';
import MosaicDetail from './pages/cameras/Mosaic/MosaicDetail';
import VideoAnalysis from './pages/cameras/VideoAnalysis/VideoAnalysis';
import VideoAnalysisDetails from './pages/cameras/VideoAnalysisDetails/index';
import CamerasReports from './pages/cameras/Reports/Reports';

// facial
import LiveAlertEventDetail from './pages/facial/LiveAlerts/LiveAlertEventDetail';
import LiveAlerts from './pages/facial/LiveAlerts/LiveAlerts';
import LiveEventDetail from './pages/facial/LiveEvents/LiveEventDetail';
import LiveEvents from './pages/facial/LiveEvents/LiveEvents';
import Markers from './pages/facial/Markers/Markers';
import IgnoredPeople from './pages/facial/IgnoredPeople/IgnoredPeople';
import PeopleList from './pages/facial/People/PeopleList';
import PersonForm from './pages/facial/PersonForm/PersonForm';
import Search from './pages/facial/Search/Search';
import SearchEventDetail from './pages/facial/Search/SearchEventDetail';

// lpr
import LiveAlertLprDetection from './pages/lpr/Detection/LiveAlertLprDetection';
import LiveEventLprDetection from './pages/lpr/Detection/LiveEventLprDetection';
import SearchLprDetection from './pages/lpr/Detection/SearchLprDetection';
import LprLiveAlerts from './pages/lpr/LiveAlerts/LiveAlerts';
import LprLiveEvents from './pages/lpr/LiveEvents/LiveEvents';
import Plates from './pages/lpr/Plates/Plates';
import LprReports from './pages/lpr/Reports/Reports';
import LprSearch from './pages/lpr/Search/Search';

// guardian app
import GuardianAppUsers from './pages/guardian-app/Users';

// events
import EventsBoard from './pages/events/EventsBoard';
import EventsOccurrenceDetail from './pages/events/EventsOccurrenceDetail';

// App Agent
import AgentSignatures from './pages/app-agent/AgentSignatures';
import AgentUnits from './pages/app-agent/AgentUnits/AgentUnits';
import ActivitiesAndServices from './pages/app-agent/ActivitiesAndServices/ActivitiesAndServices';
import CortexFinder from './pages/app-agent/CortexFinder/CortexFinder';
import AgentOccurrenceDetails from './pages/app-agent/Occurrences/AgentOccurrenceDetails';
import AgentOccurrences from './pages/app-agent/Occurrences/AgentOccurrences';
import PersonOfInterest from './pages/app-agent/PersonOfInterest';
import OpenVacancies from './pages/app-agent/Deac/OpenVacancies/OpenVacancies';
import VacancyDetailsApply from './pages/app-agent/Deac/OpenVacancies/VacancyDetailsApply';
import Candidacies from './pages/app-agent/Deac/Candidacies/Candidacies';
import VacancyDetailsCancel from './pages/app-agent/Deac/Candidacies/VacancyDetailsCancel';
import Debugger from './pages/app-agent/Debugger/Debugger';
import Services from './pages/app-agent/Services/Services';
import LprEvents from './pages/app-agent/LprEvents/LprEvents';

// Dashboard
import DashboardAgents from './pages/dashboard/DashboardAgents/DashboardAgents';
import DashboardCameras from './pages/dashboard/DashboardCameras';
import DashboardOccurrences from './pages/dashboard/DashboardOccurrences';
import DashboardVehicles from './pages/dashboard/DashboardVehicles/DashboardVehicles';
import DashboardPlateMonitoring from './pages/dashboard/DashboardPlateMonitoring/DashboardPlateMonitoring';

// Logs
import LogList from './pages/logs/LogList/LogList';
import LogReports from './pages/logs/Reports/Reports';

// Smart Search Facial
import SmartSearchFacial from './pages/smart-search-facial/SmartSearchFacial';

// User
import Account from './pages/user/Account';
import Notes from './pages/user/Notes';
import Password from './pages/user/Password';
import Sessions from './pages/user/Sessions';
import Calendar from './pages/user/Calendar';
import OwnSupportTickets from './pages/user/OwnSupportTickets';
import GetOwnSupportTicket from './pages/user/GetOwnSupportTicket';

// CRM
import AlarmCenterDetails from './pages/crm/AlarmCenterDetails';
import AlarmCentersList from './pages/crm/AlarmCentersList';
import TicketReasons from './pages/crm/TicketReason/TicketReasons';
import CameraHistory from './pages/crm/CameraHistory/index';
import MaintenanceRequests from './pages/crm/MaintenanceRequests/index';
import Management from './pages/crm/Management/Management';
import ManagementDetails from './pages/crm/ManagementDetails/ManagementDetails';
import OfflineEventsKanban from './pages/crm/OfflineEventsKanban';

// Plate Validation
import CloneSuspicions from './pages/cloned-plate/clone-suspicions/CloneSuspicions';
import ClonedPlates from './pages/cloned-plate/cloned-plates/ClonedPlates';

// Signatures
import Signatures from './pages/signatures/Signatures';

// Armory
import ArmoryShifts from './pages/armory/ArmoryShifts/ArmoryShifts';
import ArmoryShiftForm from './pages/armory/ArmoryShifts/ArmoryShiftForm';
import MyArmoryShifts from './pages/armory/MyArmoryShifts/MyArmoryShifts';

// Deac
import VacancyLevels from './pages/deac/VacancyLevels/VacancyLevels';
import Workplaces from './pages/deac/Workplaces/Workplaces';
import VacancyTags from './pages/deac/VacancyTags/VacancyTags';
import VacancyCancellationReasons from './pages/deac/VacancyCancellationReasons/VacancyCancellationReasons';
import Vacancies from './pages/deac/Vacancies/Vacancies';
import VacancyForm from './pages/deac/Vacancies/VacancyForm';
import PresenceControl from './pages/deac/PresenceControl/PresenceControl';
import DeacReports from './pages/deac/Reports/Reports';
import Dashboard from './pages/deac/Dashboard/Dashboard';
import Applications from './pages/deac/Applications/Applications';
import VacanciesTemplates from './pages/deac/VacanciesTemplates/VacanciesTemplates';
import VacancyTemplateForm from './pages/deac/VacanciesTemplates/VacancyTemplateForm';

// Third Party Approved Companies
import Inscriptions from './pages/third-party-approved-companies/Inscriptions/Inscriptions';
import InscriptionForm from './pages/third-party-approved-companies/Inscriptions/InscriptionForm';
import DonatedCamerasList from './pages/third-party-approved-companies/DonatedCameras/DonatedCamerasList';

// Citizen
import CitizenManagement from './pages/citizen/CitizenManagement/CitizenManagement';
import CitizenLprEvents from './pages/citizen/CitizenLprEvents/CitizenLprEvents';
import CitizenLprSearch from './pages/citizen/CitizenLprSearch/CitizenLprSearch';
import CitizenLprEventsDetails from './pages/citizen/CitizenLprEvents/CitizenLprEventsDetails';
import CitizenBlockReasons from './pages/citizen/CitizenBlockReasons/CitizenBlockReasons';

//Param typings
import {
    type AppAgentParamList,
    type ArmoryParamList,
    type CamerasParamList,
    type ClonedPlateParamList,
    type CRMParamList,
    type DashBoardParamList,
    type DeacParamList,
    type GuardianAppParamList,
    type LogsParamList,
    type LPRParamList,
    type RootStackParamList,
    type SignaturesParamList,
    type SmartSearchFacialParamList,
    type UserParamList,
    type ThirdPartyApprovedCompaniesParamList,
    CitizenParamList,
    ModuleParamLists,
    FacialParamList,
    EventsParamList,
    WebRtcParamList,
    StreamServersParamList,
    RootScreensParamLists,
} from './typings/Params';

import { DefaultTheme, NavigationContainer, NavigationContainerRefWithCurrent, useNavigation } from '@react-navigation/native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import DefaultPageContainer from './components/DefaultPageContainer';
import DefaultPageLayout from './components/DefaultPageLayout';
import { ActivityIndicator, ImageSourcePropType, View } from 'react-native';
import { CurrentApp, useCurrentApp } from './context/CurrentApp';
import TicketDetail from './pages/crm/TicketDetail/TicketDetail';
import zod from 'zod';
import { useStyles } from 'react-native-unistyles';
import { useAuth } from './context/Auth';
import { faWindows } from '@fortawesome/free-brands-svg-icons';
import { faBug, faGear, faSquareKanban } from '@fortawesome/pro-regular-svg-icons';
import { AppAgentSchemas } from './schemas/agent-app';
import { CamerasSchemas } from './schemas/cameras';
import { FacialSchemas } from './schemas/facial';
import { SmartSearchFacialSchemas } from './schemas/smart-search-facial';
import { LprSchemas } from './schemas/lpr';
import { ClonedPlateSchemas } from './schemas/cloned-plate';
import { EventsSchemas } from './schemas/events';
import { UserSchemas } from './schemas/user';
import { GuardianAppSchemas } from './schemas/guardian-app';
import { CrmSchemas } from './schemas/crm';
import { LogsSchemas } from './schemas/logs';
import { WebRtcSchemas } from './schemas/webrtc';
import { StreamServersSchemas } from './schemas/stream-servers';
import Publisher from './pages/webrtc/Publisher';
import Consumer from './pages/webrtc/Consumer';
import ServerFarms from './pages/stream-servers/ServerFarms';
import ServerFarmDetail from './pages/stream-servers/ServerFarmDetail';
import { DashboardSchemas } from './schemas/dashboard';
import { SignaturesSchemas } from './schemas/signatures';
import { ArmorySchemas } from './schemas/armory';
import { CitizenSchemas } from './schemas/citizen';
import { ThirdPartyApprovedCompaniesSchemas } from './schemas/third-party-approved-companies';
import { DeacSchemas } from './schemas/deac';
import { RootSchemas } from './schemas/root';
import { dispatchModule } from './routes/dispatch';
import { accessModule } from './routes/access';

const Stack = createStackNavigator<RootStackParamList>();

export interface RootNavigation {
    navigate<P extends keyof RootScreensParamLists>(
        page: P,
        params?: RootScreensParamLists[P],
    ): void;
    navigate<
        M extends keyof ModuleParamLists,
        S extends keyof ModuleParamLists[M],
    >(
        module: M,
        options?: {
            screen: S;
            params?: ModuleParamLists[M][S];
        },
    ): void;
    goBack(): void;
    getState(): {
        routeNames: string[];
    };
    addListener(event: string, action: () => void): void;
    setParams(
        params: Record<string, string | number | undefined | boolean | number[] | string[]>,
    ): void;
    canGoBack(): boolean;
}

interface Screens {
    [pageName: string]: {
        path: string;
        screens?: Screens;
        parse?: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            [queryParam: string]: any;
        };
    };
}
interface ModulePage<
    K extends keyof ModuleParamLists,
    S extends keyof ModuleParamLists[K],
> {
    path: string;
    selectedItem: string;

    initialParams?: () => ModuleParamLists[K][S];

    canAccess: (user: AuthenticatedUser | undefined | null, currentApp: CurrentApp) => boolean;
    /**
     * this will be used on Lateral Menu, Global Search Filter and Browser Tab Title
     */
    label: i18nKeys;
    component: (params: { navigation: RootNavigation; }) => React.JSX.Element;

    /**
     * if you inform this prop, this page will appear on both module Lateral Menu and Global Search Filter
     */
    accessInformation?: {
        icon: IconDefinition | ProIconDefinition;
        navigate: (nav: RootNavigation) => void;
    };
}

type ModulePageConfig<K extends keyof ModuleParamLists> = {
    [S in keyof ModuleParamLists[K]]: ModulePage<K, S>;
};

export type Module<
    K extends keyof ModuleParamLists,
> = {
    path: string;
    stackNavigator: ReturnType<typeof createStackNavigator<ModuleParamLists[K]>>;
    screens: ModulePageConfig<K>;
    schemas: Record<string, zod.AnyZodObject | zod.ZodOptional<zod.AnyZodObject>>;
    label: i18nKeys;
    canAccess: (
        user: AuthenticatedUser | undefined | null,
        currentApp: CurrentApp,
    ) => boolean;
    navigate: (nav: RootNavigation) => void;
} & ({
    hideFromMenu: true;
} | {
    hideFromMenu?: false;
    logo: ImageSourcePropType;
    icon: ImageSourcePropType;
});

type ModuleConfig = {
    [K in keyof ModuleParamLists]: Module<K>;
};

type RootScreen = {
    path: string;
    label: i18nKeys;
    canAccess: (
        user: AuthenticatedUser | undefined | null,
        currentApp: CurrentApp,
    ) => boolean;
    component: (params: { navigation: RootNavigation; route: unknown; }) => React.JSX.Element;

    /**
     * if you inform this prop, this page will appear on Global Search Filter
     */
    accessInformation?: {
        icon: IconDefinition | ProIconDefinition;
        navigate: (nav: RootNavigation) => void;
    };
};

type ScreenConfig = {
    [K in keyof RootScreensParamLists]: RootScreen;
};

export interface ApplicationPages {
    screens: ScreenConfig;
    modules: ModuleConfig;
}

interface RouteConfig {
    screens: Screens;
}

function createLinkingParse<T extends zod.ZodRawShape>(
    schema: zod.ZodObject<T> | zod.ZodOptional<zod.ZodObject<T>> | zod.ZodUndefined
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parse: Record<string, (value: string) => any> = {};

    if (schema instanceof zod.ZodUndefined) {
        return;
    }

    if (schema instanceof zod.ZodOptional) {
        return parse;
    }

    const shape = schema.shape;

    for (const key in shape) {
        if (Object.hasOwn(shape, key)) {
            parse[key] = (value: string) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const result = schema.pick({ [key]: true }).safeParse({
                    [key]: value,
                });

                if (!result.success) {
                    return undefined;
                }

                return result.data[key];
            };
        }
    }

    return parse as {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [K in keyof zod.infer<typeof schema>]: (value: string) => zod.infer<typeof schema>[K];
    };
}


interface DefaultParams {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    route: any;
}

const PagesRoutes = ({ route }: DefaultParams) => {

    const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (route?.params?.pathname) {
                const path = route.params.pathname.split('/');

                if (path.length == 3 && path[1] == 'reset-password') {
                    navigation.navigate('ResetPassword', { token: path[2] });
                }

                if (path.length == 2 && path[1] == 'terms-of-use') {
                    navigation.navigate('TermsOfUse');
                }
            }
            navigation.reset({
                index: 0,
                routes: [{ name: 'Login' }],
            });
        }, 0);

        return () => clearTimeout(timeout);
    }, [navigation, route]);

    return <></>;
};

export const applicationPages: ApplicationPages = {
    screens: {
        MainPage: {
            path: '',
            label: 'SentinelX',
            canAccess: () => true,
            component: PagesRoutes,
        },
        TermsOfUse: {
            path: '/terms-of-use',
            label: 'termsOfUse',
            canAccess: () => true,
            component: TermsOfUse,
            accessInformation: {
                icon: faFileContract,
                navigate: (navigation) => navigation.navigate('TermsOfUse'),
            }
        },
        ChangeLogs: {
            path: '/change-logs',
            label: 'changeLogs',
            canAccess: () => true,
            component: ChangeLogs,
            accessInformation: {
                icon: faCodeCommit,
                navigate: (navigation) => navigation.navigate('ChangeLogs'),
            }
        },
        ContactUs: {
            path: '/contact-us',
            label: 'contactUs',
            canAccess: (user) => Boolean(user?.permissions.create_support_ticket),
            component: ContactUs,
            accessInformation: {
                icon: faPhone,
                navigate: (navigation) => navigation.navigate('ContactUs'),
            }
        },
        FrequentlyAskedQuestions: {
            path: '/frequently-asked-questions',
            label: 'faq',
            canAccess: () => true,
            component: FrequentlyAskedQuestions,
            accessInformation: {
                icon: faCircleQuestion,
                navigate: (navigation) => navigation.navigate('FrequentlyAskedQuestions'),
            }
        },
        PublicFrequentlyAskedQuestions: {
            path: '/public-frequently-asked-questions',
            label: 'faq',
            canAccess: () => true,
            component: PublicFrequentlyAskedQuestions,
        },
        Login: {
            path: '/login',
            label: 'login',
            canAccess: () => true,
            component: Login,
        },
        Menu: {
            path: '/menu',
            label: 'menu',
            canAccess: () => true,
            component: Menu,
        },
        ResetPassword: {
            path: '/reset-password/:token',
            label: 'resetPassword',
            canAccess: () => true,
            component: ResetPassword,
        },
    },
    modules: {
        User: {
            path: '/user',
            stackNavigator: createStackNavigator<UserParamList>(),
            canAccess: (user) => Boolean(user),
            hideFromMenu: true,
            label: 'settings',
            navigate: (navigation) => navigation.navigate('User'),
            schemas: UserSchemas,
            screens: {
                Account: {
                    path: 'account',
                    selectedItem: 'account',
                    component: Account,
                    canAccess: () => true,
                    label: 'account',
                    accessInformation: {
                        icon: faGear,
                        navigate: navigation => navigation.navigate('User', { screen: 'Account' })
                    },
                },
                Password: {
                    path: 'password',
                    selectedItem: 'password',
                    component: Password,
                    canAccess: () => true,
                    label: 'changePassword',
                    accessInformation: {
                        icon: faKey,
                        navigate: navigation => navigation.navigate('User', { screen: 'Password' })
                    },
                },
                Sessions: {
                    path: 'sessions',
                    selectedItem: 'sessions',
                    component: Sessions,
                    canAccess: () => true,
                    label: 'sessions',
                    accessInformation: {
                        icon: faLock,
                        navigate: navigation => navigation.navigate('User', { screen: 'Sessions' })
                    },
                },
                Calendar: {
                    path: 'calendar',
                    selectedItem: 'calendar',
                    component: Calendar,
                    canAccess: () => true,
                    label: 'calendar',
                    accessInformation: {
                        icon: faCalendar,
                        navigate: navigation => navigation.navigate('User', { screen: 'Calendar' })
                    },
                },
                Notes: {
                    path: 'notes',
                    selectedItem: 'notes',
                    component: Notes,
                    canAccess: () => true,
                    label: 'notes',
                    accessInformation: {
                        icon: faNoteSticky,
                        navigate: navigation => navigation.navigate('User', { screen: 'Notes' })
                    },
                },
                OwnSupportTickets: {
                    path: 'support-tickets',
                    selectedItem: 'support-tickets',
                    component: OwnSupportTickets,
                    canAccess: (user) => Boolean(
                        user?.permissions.create_support_ticket
                        || user?.permissions.manage_support_tickets
                    ),
                    label: 'myTicket',
                    accessInformation: {
                        icon: faTicket,
                        navigate: navigation => navigation.navigate('User', { screen: 'OwnSupportTickets' })
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                GetOwnSupportTicket: {
                    path: 'support-ticket/:id',
                    selectedItem: 'support-tickets',
                    component: GetOwnSupportTicket,
                    canAccess: (user) => Boolean(
                        user?.permissions.create_support_ticket
                        || user?.permissions.manage_support_tickets
                    ),
                    label: 'myTicket',
                },
            }
        },
        Cameras: {
            stackNavigator: createStackNavigator<CamerasParamList>(),
            path: '/cameras',
            label: 'cameras',
            canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
            icon: require('../assets/icon_cameras.png'),
            logo: require('../assets/logo_cameras.png'),
            navigate: (navigation) => navigation.navigate('Cameras'),
            schemas: CamerasSchemas,
            screens: {
                CamerasList: {
                    path: 'cameras',
                    selectedItem: 'cameras',
                    initialParams: () => ({
                        page: 1,
                        online: true,
                        limit: 25,
                    }),
                    label: 'cameras',
                    component: CamerasList,
                    accessInformation: {
                        icon: faCameraCctv,
                        navigate: (navigation) => navigation.navigate('Cameras', { screen: 'CamerasList' }),
                    },
                    canAccess: () => true,
                },
                CameraPlayer: {
                    path: 'watch/:id',
                    selectedItem: 'cameras',
                    component: CameraPlayer,
                    label: 'camera',
                    canAccess: () => true,
                },
                CameraDetails: {
                    path: 'cameras/details/:id',
                    selectedItem: 'cameras',
                    label: 'camera',
                    component: CameraDetails,
                    canAccess: () => true,
                },
                Mosaic: {
                    path: 'mosaic',
                    selectedItem: 'mosaic',
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    label: 'mosaics',
                    component: Mosaic,
                    accessInformation: {
                        icon: faWindows,
                        navigate: (navigation) => navigation.navigate('Cameras', { screen: 'Mosaic' }),
                    },
                    canAccess: () => true,
                },
                MosaicDetail: {
                    path: 'mosaic/:id',
                    selectedItem: 'mosaic',
                    label: 'mosaic',
                    component: MosaicDetail,
                    canAccess: () => true,
                },
                CamerasMap: {
                    path: 'map',
                    selectedItem: 'map',
                    label: 'map',
                    component: CamerasMap,
                    accessInformation: {
                        icon: faMap,
                        navigate: (navigation) => navigation.navigate('Cameras', { screen: 'CamerasMap' }),
                    },
                    canAccess: () => true,
                },
                CameraMapPlayer: {
                    path: 'map/:id',
                    selectedItem: 'map',
                    component: CameraPlayer,
                    label: 'camera',
                    canAccess: () => true,
                },
                CameraTags: {
                    path: 'camera-tags',
                    label: 'tags',
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    selectedItem: 'camera-tags',
                    component: CameraTags,
                    canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_manage_tags),
                    accessInformation: {
                        icon: faTag,
                        navigate: (navigation) => navigation.navigate('Cameras', { screen: 'CameraTags' }),
                    }
                },
                DownloadTags: {
                    path: 'download-tags',
                    selectedItem: 'download-tags',
                    label: 'downloadsTags',
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    component: DownloadTags,
                    canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_manage_download_tags),
                    accessInformation: {
                        icon: faTag,
                        navigate: (navigation) => navigation.navigate('Cameras', { screen: 'DownloadTags' }),
                    }
                },
                Downloads: {
                    path: 'downloads',
                    selectedItem: 'downloads',
                    label: 'downloads',
                    component: Downloads,
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_download_video || user?.permissions.camera_watch_download),
                    accessInformation: {
                        icon: faDownload,
                        navigate: (navigation) => navigation.navigate('Cameras', { screen: 'Downloads' }),
                    }
                },
                VideoAnalysis: {
                    path: 'video-analysis',
                    selectedItem: 'video-analysis',
                    label: 'zeusAI',
                    component: VideoAnalysis,
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_video_analysis || user?.permissions.camera_watch_video_analysis),
                    accessInformation: {
                        icon: faMagnifyingGlass,
                        navigate: (navigation) => navigation.navigate('Cameras', { screen: 'VideoAnalysis' }),
                    }
                },
                VideoAnalysisDetails: {
                    path: 'video-analysis/details/:id',
                    selectedItem: 'video-analysis',
                    label: 'zeusAI',
                    component: VideoAnalysisDetails,
                    canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_video_analysis || user?.permissions.camera_watch_video_analysis),
                },
                AlertGroups: {
                    path: 'alert-groups',
                    selectedItem: 'alert-groups',
                    label: 'alertGroups',
                    component: AlertGroups,
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.manage_camera_alert_groups),
                    accessInformation: {
                        icon: faBell,
                        navigate: (navigation) => navigation.navigate('Cameras', { screen: 'AlertGroups' }),
                    }
                },
                CamerasReports: {
                    path: 'cameras-reports',
                    selectedItem: 'cameras-reports',
                    label: 'reports',
                    component: CamerasReports,
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(user?.permissions.camera_offline_historic_report),
                    accessInformation: {
                        icon: faFolderOpen,
                        navigate: (navigation) => navigation.navigate('Cameras', { screen: 'CamerasReports' }),
                    }
                }
            },
        },
        Facial: {
            stackNavigator: createStackNavigator<FacialParamList>(),
            path: '/facial',
            canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
            icon: require('../assets/icon_facial.png'),
            logo: require('../assets/logo_facial.png'),
            navigate: (navigation) => navigation.navigate('Facial'),
            label: 'facial',
            schemas: FacialSchemas,
            screens: {
                LiveEvents: {
                    path: 'live-events',
                    selectedItem: 'live-events',
                    component: LiveEvents,
                    label: 'liveEvents',
                    accessInformation: {
                        icon: faClipboardList,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'LiveEvents' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_events_missing_person
                        || user?.permissions.facial_events_wanted_person
                    )
                },
                LiveEventDetail: {
                    path: 'live-events/event/:eventId',
                    selectedItem: 'live-events',
                    component: LiveEventDetail,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_events_missing_person
                        || user?.permissions.facial_events_wanted_person
                    ),
                },
                LiveAlerts: {
                    path: 'live-alerts',
                    selectedItem: 'live-alerts',
                    component: LiveAlerts,
                    label: 'liveAlerts',
                    accessInformation: {
                        icon: faTriangleExclamation,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'LiveAlerts' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_alerts_missing_person
                        || user?.permissions.facial_alerts_wanted_person
                    )
                },
                LiveAlertEventDetail: {
                    path: 'live-alerts/event/:eventId',
                    component: LiveAlertEventDetail,
                    selectedItem: 'live-alerts',
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_alerts_missing_person
                        || user?.permissions.facial_alerts_wanted_person
                    )
                },
                Search: {
                    path: 'search',
                    selectedItem: 'search',
                    component: Search,
                    label: 'search',
                    initialParams: () => ({
                        onlyWithPerson: true,
                        endIn: moment().toDate().getTime(),
                    }),
                    accessInformation: {
                        icon: faSearch,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'Search' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_alerts_missing_person
                        || user?.permissions.facial_alerts_wanted_person
                        || user?.permissions.facial_events_missing_person
                        || user?.permissions.facial_events_wanted_person
                    ),
                },
                SearchEventDetail: {
                    path: 'search/event/:eventId',
                    selectedItem: 'search',
                    component: SearchEventDetail,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_alerts_missing_person
                        || user?.permissions.facial_alerts_wanted_person
                        || user?.permissions.facial_events_missing_person
                        || user?.permissions.facial_events_wanted_person
                    ),
                },
                PeopleList: {
                    path: 'people',
                    selectedItem: 'people',
                    component: PeopleList,
                    label: 'facialRegistration',
                    initialParams: () => ({
                        page: 1,
                        onlyActive: true,
                        limit: 25,
                    }),
                    accessInformation: {
                        icon: faIdBadge,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'PeopleList' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.manage_missing_person
                        || user?.permissions.manage_wanted_person
                    ),
                },
                PersonForm: {
                    path: '/people/form/:id?',
                    selectedItem: 'people',
                    component: PersonForm,
                    label: 'registerPerson',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.manage_missing_person
                        || user?.permissions.manage_wanted_person
                    ),
                },
                Markers: {
                    path: 'markers',
                    selectedItem: 'markers',
                    component: Markers,
                    label: 'tags',
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    accessInformation: {
                        icon: faTag,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'Markers' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_manage_tags
                    ),
                },
                IgnoredPeople: {
                    path: 'ignored-people',
                    selectedItem: 'ignored-people',
                    component: IgnoredPeople,
                    label: 'ignoredList',
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    accessInformation: {
                        icon: faUserLock,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'IgnoredPeople' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                    ),
                },
            }
        },
        SmartSearchFacial: {
            path: '/smart-search-facial',
            stackNavigator: createStackNavigator<SmartSearchFacialParamList>(),
            canAccess: (user, currentApp) => Boolean((user?.isAdmin || user?.permissions.smart_search_module_access) && !currentApp.isAgentApp),
            icon: require('../assets/icon_smart_search_facial.png'),
            logo: require('../assets/logo_smart_search_facial.png'),
            navigate: (navigation) => navigation.navigate('SmartSearchFacial'),
            label: 'smartSearchFacial',
            schemas: SmartSearchFacialSchemas,
            screens: {
                SmartSearchFacial: {
                    path: 'search',
                    selectedItem: 'search',
                    canAccess: () => true,
                    component: SmartSearchFacial,
                    label: 'searchByPhoto',
                    accessInformation: {
                        icon: faSearch,
                        navigate: (navigation) => navigation.navigate('SmartSearchFacial', { screen: 'SmartSearchFacial' }),
                    }
                },
            }
        },
        LPR: {
            path: '/LPR',
            stackNavigator: createStackNavigator<LPRParamList>(),
            icon: require('../assets/icon_lpr.png'),
            logo: require('../assets/logo_lpr.png'),
            canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
            label: 'lpr',
            navigate: (navigation) => navigation.navigate('LPR'),
            schemas: LprSchemas,
            screens: {
                LprLiveEvents: {
                    path: 'live-events',
                    selectedItem: 'live-events',
                    component: LprLiveEvents,
                    label: 'liveEvents',
                    accessInformation: {
                        icon: faClipboardList,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'LprLiveEvents' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                LiveEventLprDetection: {
                    path: 'live-events/lpr-detection/:detectionId',
                    selectedItem: 'live-events',
                    component: LiveEventLprDetection,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                LprLiveAlerts: {
                    path: 'live-alerts',
                    selectedItem: 'live-alerts',
                    component: LprLiveAlerts,
                    label: 'liveAlerts',
                    accessInformation: {
                        icon: faTriangleExclamation,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'LprLiveAlerts' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                LiveAlertLprDetection: {
                    path: 'live-alerts/lpr-detection/:detectionId',
                    selectedItem: 'live-alerts',
                    component: LiveAlertLprDetection,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                LprSearch: {
                    path: 'search',
                    selectedItem: 'search',
                    component: LprSearch,
                    label: 'search',
                    initialParams: () => ({
                        endIn: moment().toDate().getTime(),
                        irregularSituation: false,
                        isMotorcycle: false,
                    }),
                    accessInformation: {
                        icon: faSearch,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'LprSearch' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                SearchLprDetection: {
                    path: 'search/lpr-detection/:detectionId',
                    selectedItem: 'search',
                    component: SearchLprDetection,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                Plates: {
                    path: 'plates',
                    selectedItem: 'plates',
                    component: Plates,
                    label: 'plates',
                    accessInformation: {
                        icon: faCar,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'Plates' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                        onlyActive: true,
                    }),
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_edit_denylist
                        || user?.permissions.lpr_see_denylist
                        || user?.permissions.lpr_edit_illegal_debris
                        || user?.permissions.lpr_see_illegal_debris
                    )

                },
                LprReports: {
                    path: 'reports',
                    selectedItem: 'reports',
                    component: LprReports,
                    label: 'reports',
                    accessInformation: {
                        icon: faFolderOpen,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'LprReports' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                    )
                },
            }
        },
        ClonedPlate: {
            path: '/cloned-plate',
            stackNavigator: createStackNavigator<ClonedPlateParamList>(),
            icon: require('../assets/icon_cloned_plate.png'),
            logo: require('../assets/logo_cloned_plate.png'),
            canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
            label: 'clonedPlate',
            navigate: (navigation) => navigation.navigate('ClonedPlate'),
            schemas: ClonedPlateSchemas,
            screens: {
                ClonedPlates: {
                    path: 'cloned-plates',
                    selectedItem: 'cloned-plates',
                    component: ClonedPlates,
                    label: 'clonedPlates',
                    accessInformation: {
                        icon: faCar,
                        navigate: (navigation) => navigation.navigate('ClonedPlate', { screen: 'ClonedPlates' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.cloned_plate_module_access
                    )
                },
                CloneSuspicions: {
                    path: 'clone-suspicions',
                    selectedItem: 'clone-suspicions',
                    component: CloneSuspicions,
                    label: 'cloneSuspicions',
                    accessInformation: {
                        icon: faClipboard,
                        navigate: (navigation) => navigation.navigate('ClonedPlate', { screen: 'CloneSuspicions' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.cloned_plate_module_access
                    )
                },
            }
        },
        Events: {
            path: '/events',
            stackNavigator: createStackNavigator<EventsParamList>(),
            canAccess: (user, currentApp) => Boolean(!currentApp.isAgentApp && (
                user?.isAdmin ||
                user?.permissions.events_module_access ||
                user?.permissions.events_manage_missing_face_occurrence ||
                user?.permissions.events_manage_not_allowed__face_occurrence ||
                user?.permissions.events_manage_wanted_face_occurrence ||
                user?.permissions.events_manage_wanted_plate_occurrence ||
                user?.permissions.events_manage_wanted_plate_by_mobile_agent_occurrence ||
                user?.permissions.events_manage_irregular_vehicle_occurrence ||
                user?.permissions.events_manage_clone_suspect_occurrence ||
                user?.permissions.events_manage_illegal_debris_occurrence ||
                user?.permissions.events_manage_area_invasion_occurrence ||
                user?.permissions.events_manage_mp_wanted_plate_occurrence ||
                user?.permissions.events_manage_mp_wanted_mobile_agent_plate_occurrence ||
                user?.permissions.events_manage_mp_wanted_face_occurrence ||
                user?.permissions.events_manage_alarm_center_occurrence ||
                user?.permissions.events_manage_guardian_app_occurrence ||
                user?.permissions.events_manage_school_app_occurrence ||
                user?.permissions.events_manage_sigrc_occurrence ||
                user?.permissions.events_manage_cortex_plate_occurrence ||
                user?.permissions.events_manage_manual_occurrence ||
                user?.permissions.events_manage_camera_depredation_occurrence
            )),
            icon: require('../assets/icon_events.png'),
            logo: require('../assets/logo_events.png'),
            label: 'events',
            navigate: (navigation) => navigation.navigate('Events'),
            schemas: EventsSchemas,
            screens: {
                EventsBoard: {
                    path: 'events',
                    selectedItem: 'events',
                    component: EventsBoard,
                    label: 'events',
                    initialParams: () => ({
                        beginIn: moment().subtract(12, 'hours').valueOf(),
                    }),
                    accessInformation: {
                        icon: faIdBadge,
                        navigate: (navigation) => navigation.navigate('Events', { screen: 'EventsBoard' }),
                    },
                    canAccess: () => true,
                },
                EventsOccurrenceDetail: {
                    path: 'events/detail/:occurrenceId',
                    selectedItem: 'events',
                    component: EventsOccurrenceDetail,
                    label: 'occurrences',
                    canAccess: () => true,
                },
            }
        },
        Dispatch: dispatchModule,
        AppAgent: {
            path: '/app-agent',
            stackNavigator: createStackNavigator<AppAgentParamList>(),
            canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.receive_occurrences),
            icon: require('../assets/icon_app_agent.png'),
            logo: require('../assets/logo_app_agent.png'),
            label: 'appAgent',
            navigate: (navigation) => navigation.navigate('AppAgent'),
            schemas: AppAgentSchemas,
            screens: {
                AgentOccurrences: {
                    path: 'occurrences',
                    selectedItem: 'occurrences',
                    component: AgentOccurrences,
                    label: 'occurrences',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faClipboardList,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'AgentOccurrences' }),
                    }
                },
                AgentOccurrenceDetails: {
                    path: 'occurrences/:occurrenceId',
                    selectedItem: 'occurrences',
                    component: AgentOccurrenceDetails,
                    label: 'occurrences',
                    canAccess: () => true,
                },
                AgentUnits: {
                    path: 'units',
                    selectedItem: 'units',
                    component: AgentUnits,
                    label: 'teams',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faShield,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'AgentUnits' }),
                    }
                },
                ActivitiesAndServices: {
                    path: 'activities-and-services',
                    selectedItem: 'activities-and-services',
                    canAccess: () => true,
                    component: ActivitiesAndServices,
                    label: 'activitiesAndServices',
                    accessInformation: {
                        icon: faUserPolice,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'ActivitiesAndServices' }),
                    }
                },
                CortexFinder: {
                    path: 'cortex-finder',
                    selectedItem: 'cortex-finder',
                    canAccess: (user) => Boolean(
                        user?.isAdmin ||
                        user?.permissions.search_people_cortex ||
                        user?.permissions.search_cortex_any_time
                    ),
                    component: CortexFinder,
                    label: 'searchOnCortex',
                    accessInformation: {
                        icon: faSearch,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'CortexFinder' }),
                    }
                },
                PersonOfInterest: {
                    path: 'person-of-interest',
                    selectedItem: 'person-of-interest',
                    canAccess: (user, currentApp) => Boolean(
                        currentApp.isParentApp
                    ),
                    component: PersonOfInterest,
                    label: 'personOfInterest',
                    accessInformation: {
                        icon: faIdBadge,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'PersonOfInterest' }),
                    },
                },
                AgentSignatures: {
                    path: 'signatures',
                    selectedItem: 'signatures',
                    canAccess: () => true,
                    component: AgentSignatures,
                    label: 'signatures',
                    accessInformation: {
                        icon: faFileSignature,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'AgentSignatures' }),
                    },
                },
                OpenVacancies: {
                    path: 'open-vacancies',
                    selectedItem: 'open-vacancies',
                    canAccess: (user) => Boolean(user?.permissions.app_agent_deac_access),
                    component: OpenVacancies,
                    label: 'openVacancies',
                    initialParams: () => ({
                        showOnlyAvailableVacancies: true,
                    }),
                    accessInformation: {
                        icon: faMemoCircleInfo,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'OpenVacancies' }),
                    },
                },
                VacancyDetailsApply: {
                    path: 'vacancy-details/apply/:id',
                    selectedItem: 'open-vacancies',
                    canAccess: (user) => Boolean(user?.permissions.app_agent_deac_access),
                    component: VacancyDetailsApply,
                    label: 'vacancyDetails',
                },
                Candidacies: {
                    path: 'my-candidacies',
                    selectedItem: 'my-candidacies',
                    canAccess: (user) => Boolean(user?.permissions.app_agent_deac_access),
                    component: Candidacies,
                    label: 'myCandidacies',
                    initialParams: () => ({
                        beginIn: moment().startOf('year').valueOf(),
                        endIn: moment().endOf('year').valueOf(),
                    }),
                    accessInformation: {
                        icon: faMemoCircleCheck,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'Candidacies' }),
                    },
                },
                VacancyDetailsCancel: {
                    path: 'vacancy-details/cancel/:id',
                    selectedItem: 'my-candidacies',
                    canAccess: (user) => Boolean(user?.permissions.app_agent_deac_access),
                    component: VacancyDetailsCancel,
                    label: 'vacancyDetails',
                },
                Debugger: {
                    path: 'debugger',
                    selectedItem: 'debugger',
                    canAccess: () => Boolean(window.AndroidBridge),
                    component: Debugger,
                    label: 'debugger',
                    initialParams: () => ({
                        tags: [],
                        subTags: [],
                    }),
                    accessInformation: {
                        icon: faBug,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'Debugger' }),
                    },
                },
                Services: {
                    path: 'services',
                    selectedItem: 'services',
                    canAccess: () => Boolean(window.AndroidBridge),
                    component: Services,
                    label: 'services',
                    accessInformation: {
                        icon: faGear,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'Services' }),
                    },

                },
                LprEvents: {
                    path: 'lpr-events',
                    selectedItem: 'lpr-events',
                    canAccess: (user) => Boolean(window.AndroidBridge && user?.permissions.app_agent_manage_services),
                    component: LprEvents,
                    label: 'lprEvents',
                    accessInformation: {
                        icon: faCar,
                        navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'LprEvents' }),
                    },

                }
            }
        },
        GuardianApp: {
            path: '/guardian-app',
            stackNavigator: createStackNavigator<GuardianAppParamList>(),
            canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
            icon: require('../assets/icon_guardian_app.png'),
            logo: require('../assets/logo_guardian_app.png'),
            label: 'guardianApp',
            navigate: (navigation) => navigation.navigate('GuardianApp'),
            schemas: GuardianAppSchemas,
            screens: {
                GuardianAppUsers: {
                    path: 'users',
                    selectedItem: 'users',
                    component: GuardianAppUsers,
                    label: 'users',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.guardian_app_module_access
                    ),
                    accessInformation: {
                        icon: faVenus,
                        navigate: (navigation) => navigation.navigate('GuardianApp', { screen: 'GuardianAppUsers' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
            }
        },
        Access: accessModule,
        CRM: {
            path: '/CRM',
            stackNavigator: createStackNavigator<CRMParamList>(),
            canAccess: (user, currentApp) => Boolean(
                !currentApp.isAgentApp && (
                    user?.isAdmin
                    || user?.permissions.crm_module_access
                )
            ),
            icon: require('../assets/icon_crm.png'),
            logo: require('../assets/logo_crm.png'),
            label: 'crm',
            navigate: (navigation) => navigation.navigate('CRM'),
            schemas: CrmSchemas,
            screens: {
                Management: {
                    path: 'cameras',
                    selectedItem: 'cameras',
                    component: Management,
                    label: 'cameras',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faCameraCctv,
                        navigate: (navigation) => navigation.navigate('CRM', { screen: 'Management' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
                ManagementDetails: {
                    path: 'cameras/details/:id?',
                    selectedItem: 'cameras',
                    component: ManagementDetails,
                    label: 'cameras',
                    canAccess: () => true,
                },
                CameraHistory: {
                    path: 'cameras/history/:id',
                    selectedItem: 'cameras',
                    component: CameraHistory,
                    label: 'cameraHistory',
                    canAccess: () => true,
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                        orderBy: 'start',
                        orderDirection: 'asc',
                        onlyOffline: false,
                    }),
                },
                AlarmCentersList: {
                    path: 'alarm-centers',
                    selectedItem: 'alarm-centers',
                    component: AlarmCentersList,
                    label: 'alarmCenters',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faCalculator,
                        navigate: (navigation) => navigation.navigate('CRM', { screen: 'AlarmCentersList' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
                AlarmCenterDetails: {
                    path: 'alarm-centers/details/:id?',
                    selectedItem: 'alarm-centers',
                    component: AlarmCenterDetails,
                    label: 'alarmCenters',
                    canAccess: () => true,
                },
                OfflineEvents: {
                    path: 'cameras/history',
                    selectedItem: 'offline-events',
                    component: CameraHistory,
                    label: 'offlineEvents',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faPlug,
                        navigate: (navigation) => navigation.navigate('CRM', { screen: 'OfflineEvents' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                        orderBy: 'start',
                        orderDirection: 'asc',
                        onlyOffline: false,
                    }),
                },
                OfflineEventsKanban: {
                    path: 'offline-events-kanban',
                    selectedItem: 'offline-events-kanban',
                    component: OfflineEventsKanban,
                    label: 'offlineEventsKanban',
                    canAccess: (user, currentApp) => currentApp.isParentApp,
                    accessInformation: {
                        icon: faSquareKanban,
                        navigate: (navigation) => navigation.navigate('CRM', { screen: 'OfflineEventsKanban' }),
                    },
                },
                KanbanTicketDetail: {
                    path: 'offline-events-kanban/:id',
                    selectedItem: 'offline-events-kanban',
                    component: TicketDetail,
                    label: 'offlineEventsKanban',
                    canAccess: (user, currentApp) => currentApp.isParentApp,
                },
                TicketCameraDetail: {
                    path: 'offline-ticket/camera/:id',
                    selectedItem: 'cameras',
                    component: TicketDetail,
                    label: 'offlineEventsKanban',
                    canAccess: () => true,
                },
                TicketDetail: {
                    path: 'offline-ticket/:id',
                    selectedItem: 'offline-events',
                    component: TicketDetail,
                    label: 'offlineEventsKanban',
                    canAccess: () => true,
                },
                MaintenanceRequests: {
                    path: 'maintenance-requests',
                    selectedItem: 'maintenance-requests',
                    component: MaintenanceRequests,
                    label: 'maintenanceRequests',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faScrewdriverWrench,
                        navigate: (navigation) => navigation.navigate('CRM', { screen: 'MaintenanceRequests' }),
                    },
                },
                TicketReasons: {
                    path: 'ticket-reasons',
                    selectedItem: 'ticket-reasons',
                    component: TicketReasons,
                    label: 'drpScenarios',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faCameraSlash,
                        navigate: (navigation) => navigation.navigate('CRM', { screen: 'TicketReasons' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
            }
        },
        Logs: {
            path: '/logs',
            stackNavigator: createStackNavigator<LogsParamList>(),
            canAccess: (user, currentApp) => Boolean(
                !currentApp.isAgentApp && (
                    user?.isAdmin
                    || user?.permissions.logs_module_access
                )
            ),
            icon: require('../assets/icon_logs.png'),
            logo: require('../assets/logo_logs.png'),
            label: 'logs',
            navigate: (navigation) => navigation.navigate('Logs'),
            schemas: LogsSchemas,
            screens: {
                LogList: {
                    path: 'search',
                    selectedItem: 'search',
                    component: LogList,
                    label: 'logRegistries',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faFileContract,
                        navigate: (navigation) => navigation.navigate('Logs', { screen: 'LogList' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                        dateLte: moment().endOf('day').valueOf(),
                        dateGte: moment().startOf('day').valueOf(),
                        sort: 'desc',
                    }),
                },
                LogReports: {
                    path: 'reports',
                    selectedItem: 'reports',
                    component: LogReports,
                    label: 'reports',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faFolderOpen,
                        navigate: (navigation) => navigation.navigate('Logs', { screen: 'LogReports' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
            }
        },
        Dashboard: {
            path: '/dashboard',
            stackNavigator: createStackNavigator<DashBoardParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_dashboard.png'),
            logo: require('../assets/logo_dashboard.png'),
            label: 'dashboard',
            navigate: (navigation) => navigation.navigate('Dashboard'),
            schemas: DashboardSchemas,
            screens: {
                DashboardOccurrences: {
                    selectedItem: 'occurrences',
                    path: 'occurrences',
                    component: DashboardOccurrences,
                    label: 'occurrences',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.dashboard_module_access
                        || user?.permissions.dashboard_see_occurrence
                    ),
                    accessInformation: {
                        icon: faClipboardList,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardOccurrences' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                        beginIn: moment().subtract(12, 'hours').valueOf(),
                    }),
                },
                DashboardPlateMonitoring: {
                    selectedItem: 'plate-monitoring',
                    path: 'plate-monitoring',
                    component: DashboardPlateMonitoring,
                    label: 'plateMonitoring',
                    canAccess: (user) => Boolean(
                        user?.permissions.dashboard_module_access
                        || user?.permissions.dashboard_manage_monitoring_plates_groups
                        || user?.permissions.dashboard_manage_monitoring_plates
                        || user?.permissions.dashboard_see_monitoring_plates
                    ),
                    accessInformation: {
                        icon: faCarBus,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardPlateMonitoring' }),
                    },
                },
                DashboardCameras: {
                    selectedItem: 'cameras',
                    path: 'cameras',
                    component: DashboardCameras,
                    label: 'cameras',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.dashboard_module_access
                        || user?.permissions.dashboard_see_cameras
                    ),
                    accessInformation: {
                        icon: faCameraCctv,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardCameras' }),
                    },
                },
                DashboardAgents: {
                    selectedItem: 'agents',
                    path: 'agents',
                    component: DashboardAgents,
                    label: 'agents',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.dashboard_module_access
                        || user?.permissions.dashboard_see_agents
                        || user?.permissions.dashboard_see_agents_acting_bodies
                        || user?.permissions.dashboard_see_agents_acting_body_units
                    ),
                    accessInformation: {
                        icon: faCameraCctv,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardAgents' }),
                    },
                },
                DashboardVehicles: {
                    selectedItem: 'vehicles',
                    path: 'vehicles',
                    component: DashboardVehicles,
                    label: 'vehicles',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.dashboard_see_vehicles
                        || user?.permissions.dashboard_see_vehicles_acting_bodies
                        || user?.permissions.dashboard_see_vehicles_acting_body_units
                    ),
                    accessInformation: {
                        icon: faCar,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardVehicles' }),
                    },
                },
            }
        },
        Signatures: {
            path: '/signatures',
            stackNavigator: createStackNavigator<SignaturesParamList>(),
            canAccess: (user, currentApp) => Boolean(
                !currentApp.isAgentApp && (
                    user?.isAdmin ||
                    user?.permissions.supervisor_sign_report ||
                    user?.permissions.supervisor_sign_report_unit
                )
            ),
            icon: require('../assets/icon_signatures.png'),
            logo: require('../assets/logo_signatures.png'),
            label: 'signatures',
            navigate: (navigation) => navigation.navigate('Signatures'),
            schemas: SignaturesSchemas,
            screens: {
                Signatures: {
                    path: 'signatures',
                    selectedItem: 'signatures',
                    component: Signatures,
                    label: 'signatures',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faFileSignature,
                        navigate: (navigation) => navigation.navigate('Signatures', { screen: 'Signatures' }),
                    },
                    initialParams: () => ({
                        limit: 25,
                        page: 1,
                    }),
                }
            },
        },
        Armory: {
            path: '/armory',
            stackNavigator: createStackNavigator<ArmoryParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_armory.png'),
            logo: require('../assets/logo_armory.png'),
            label: 'armory',
            navigate: (navigation) => navigation.navigate('Armory'),
            schemas: ArmorySchemas,
            screens: {
                ArmoryShifts: {
                    path: 'armory-shifts',
                    selectedItem: 'armory-shifts',
                    component: ArmoryShifts,
                    label: 'manageShifts',
                    canAccess: (user) => Boolean(
                        user?.permissions.armory_manage_armory_shift ||
                        user?.permissions.armory_manage_acting_body_units_armory_shift ||
                        user?.permissions.armory_manage_all_acting_bodies_armory_shift ||
                        user?.permissions.armory_replace_in_charge_armourer
                    ),
                    accessInformation: {
                        icon: faClock,
                        navigate: (navigation) => navigation.navigate('Armory', { screen: 'ArmoryShifts' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
                ArmoryShiftForm: {
                    path: 'armory-shifts/form/:id?',
                    selectedItem: 'armory-shifts',
                    component: ArmoryShiftForm,
                    label: 'manageShifts',
                    canAccess: (user) => Boolean(
                        user?.permissions.armory_manage_armory_shift ||
                        user?.permissions.armory_manage_acting_body_units_armory_shift ||
                        user?.permissions.armory_manage_all_acting_bodies_armory_shift ||
                        user?.permissions.armory_replace_in_charge_armourer
                    ),
                },
                MyArmoryShifts: {
                    path: 'my-armory-shifts',
                    selectedItem: 'my-armory-shifts',
                    component: MyArmoryShifts,
                    label: 'myShifts',
                    canAccess: (user) => Boolean(
                        user?.permissions.armory_assigned_as_armourer
                    ),
                    accessInformation: {
                        icon: faClipboardListCheck,
                        navigate: (navigation) => navigation.navigate('Armory', { screen: 'MyArmoryShifts' }),
                    },
                }
            },
        },
        Deac: {
            path: '/deac',
            stackNavigator: createStackNavigator<DeacParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_deac.png'),
            logo: require('../assets/logo_deac.png'),
            label: 'deac',
            navigate: (navigation) => navigation.navigate('Deac'),
            schemas: DeacSchemas,
            screens: {
                Dashboard: {
                    path: 'dashboard',
                    selectedItem: 'dashboard',
                    component: Dashboard,
                    label: 'dashboard',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_dashboard ||
                        user?.permissions.deac_manage_acting_body_units_dashboard ||
                        user?.permissions.deac_manage_all_acting_bodies_dashboard
                    ),
                    accessInformation: {
                        icon: faChartPieSimple,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'Dashboard' }),
                    },
                    initialParams: () => ({
                        beginIn: moment().startOf('day').subtract(30, 'days').valueOf(),
                        endIn: moment().endOf('day').valueOf(),
                    })
                },
                Vacancies: {
                    path: 'vacancies',
                    selectedItem: 'vacancies',
                    component: Vacancies,
                    label: 'vacanciesManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_vacancies ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancies ||
                        user?.permissions.deac_manage_vacancies ||
                        user?.permissions.deac_visualize_vacancies
                    ),
                    accessInformation: {
                        icon: faFilePen,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'Vacancies' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyForm: {
                    path: 'vacancies-form/:id?',
                    selectedItem: 'vacancies',
                    component: VacancyForm,
                    label: 'vacanciesManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_vacancies ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancies ||
                        user?.permissions.deac_manage_vacancies ||
                        user?.permissions.deac_visualize_vacancies
                    ),
                },
                Applications: {
                    path: 'applications',
                    selectedItem: 'applications',
                    component: Applications,
                    label: 'candidacies',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_applications ||
                        user?.permissions.deac_manage_acting_body_units_applications ||
                        user?.permissions.deac_manage_all_acting_bodies_applications
                    ),
                    accessInformation: {
                        icon: faMemoCircleCheck,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'Applications' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacanciesTemplates: {
                    path: 'vacancies-templates',
                    selectedItem: 'vacancies-templates',
                    component: VacanciesTemplates,
                    label: 'vacanciesTemplates',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_vacancies_templates ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancies_templates ||
                        user?.permissions.deac_manage_vacancies_templates
                    ),
                    accessInformation: {
                        icon: faHexagonNodesBolt,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'VacanciesTemplates' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyTemplateForm: {
                    path: 'vacancies-templates-form/:id?',
                    selectedItem: 'vacancies-templates',
                    component: VacancyTemplateForm,
                    label: 'vacanciesTemplates',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_vacancies_templates ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancies_templates ||
                        user?.permissions.deac_manage_vacancies_templates
                    ),
                },
                PresenceControl: {
                    path: 'presence-control',
                    selectedItem: 'presence-control',
                    component: PresenceControl,
                    label: 'presenceControl',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_presence_control ||
                        user?.permissions.deac_manage_acting_body_units_presence_control ||
                        user?.permissions.deac_manage_all_acting_bodies_presence_control
                    ),
                    accessInformation: {
                        icon: faMemoCircleCheck,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'PresenceControl' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyLevels: {
                    path: 'vacancy-levels',
                    selectedItem: 'vacancy-levels',
                    component: VacancyLevels,
                    label: 'vacancyLevels',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_vacancy_level ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancy_level
                    ),
                    accessInformation: {
                        icon: faSitemap,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'VacancyLevels' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                Workplaces: {
                    path: 'workplaces',
                    selectedItem: 'workplaces',
                    component: Workplaces,
                    label: 'workplaces',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_workplaces ||
                        user?.permissions.deac_manage_acting_body_units_workplaces ||
                        user?.permissions.deac_manage_all_acting_bodies_workplaces
                    ),
                    accessInformation: {
                        icon: faBuildingShield,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'Workplaces' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyTags: {
                    path: 'vacancy-tags',
                    selectedItem: 'vacancy-tags',
                    component: VacancyTags,
                    label: 'vacancyTags',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_vacancy_tags
                    ),
                    accessInformation: {
                        icon: faTag,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'VacancyTags' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyCancellationReasons: {
                    path: 'vacancy-cancellation-reasons',
                    selectedItem: 'vacancy-cancellation-reasons',
                    component: VacancyCancellationReasons,
                    label: 'vacancyCancellationReasons',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_vacancy_cancellation_reasons
                    ),
                    accessInformation: {
                        icon: faBan,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'VacancyCancellationReasons' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                DeacReports: {
                    path: 'reports',
                    selectedItem: 'reports',
                    component: DeacReports,
                    label: 'reports',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_reports ||
                        user?.permissions.deac_manage_all_acting_bodies_reports
                    ),
                    accessInformation: {
                        icon: faFolderOpen,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'DeacReports' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
            }
        },
        ThirdPartyApprovedCompanies: {
            path: '/third-party-approved-companies',
            stackNavigator: createStackNavigator<ThirdPartyApprovedCompaniesParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_approved_companies.png'),
            logo: require('../assets/logo_approved_companies.png'),
            label: 'approvedCompanies',
            navigate: (navigation) => navigation.navigate('ThirdPartyApprovedCompanies'),
            schemas: ThirdPartyApprovedCompaniesSchemas,
            screens: {
                InscriptionsManagement: {
                    path: 'inscriptions-management',
                    selectedItem: 'inscriptions-management',
                    component: Inscriptions,
                    label: 'inscriptionsManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.third_party_approved_companies_manage_inscriptions
                    ),
                    accessInformation: {
                        icon: faFilePen,
                        navigate: (navigation) => navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'InscriptionsManagement' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
                InscriptionForm: {
                    path: 'inscription-form/:id?',
                    selectedItem: 'inscriptions-management',
                    component: InscriptionForm,
                    label: 'inscriptionsManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.third_party_approved_companies_manage_inscriptions
                    ),
                },
                DonatedCamerasList: {
                    path: 'donated-cameras',
                    selectedItem: 'donated-cameras',
                    component: DonatedCamerasList,
                    label: 'donatedCameras',
                    canAccess: (user) => Boolean(
                        user?.permissions.manage_third_party_donated_cameras
                    ),
                    accessInformation: {
                        icon: faHandHoldingBox,
                        navigate: (navigation) => navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'DonatedCamerasList' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
            }
        },
        Citizen: {
            path: '/citizen',
            stackNavigator: createStackNavigator<CitizenParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_sentinel_app.png'),
            logo: require('../assets/logo_citizen_app.png'),
            label: 'citizenApp',
            navigate: (navigation) => navigation.navigate('Citizen'),
            schemas: CitizenSchemas,
            screens: {
                CitizenManagement: {
                    path: 'citizen-management',
                    selectedItem: 'citizen-management',
                    component: CitizenManagement,
                    label: 'citizenManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_management
                    ),
                    accessInformation: {
                        icon: faUser,
                        navigate: (navigation) => navigation.navigate('Citizen', { screen: 'CitizenManagement' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
                CitizenLprSearch: {
                    path: 'citizen-lpr-search',
                    selectedItem: 'citizen-lpr-search',
                    component: CitizenLprSearch,
                    label: 'plateSearch',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_watch_lpr_events
                    ),
                    accessInformation: {
                        icon: faMagnifyingGlass,
                        navigate: (navigation) => navigation.navigate('Citizen', { screen: 'CitizenLprSearch' }),
                    },
                    initialParams: () => ({
                        endIn: moment().valueOf(),
                    }),
                },
                CitizenLprEvents: {
                    path: 'citizen-lpr-events',
                    selectedItem: 'citizen-lpr-events',
                    component: CitizenLprEvents,
                    label: 'citizenLprEvents',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_watch_lpr_events
                    ),
                    accessInformation: {
                        icon: faCar,
                        navigate: (navigation) => navigation.navigate('Citizen', { screen: 'CitizenLprEvents' }),
                    },
                },
                CitizenLprEventsDetails: {
                    path: 'citizen-lpr-events/details/:id',
                    selectedItem: 'citizen-lpr-events',
                    component: CitizenLprEventsDetails,
                    label: 'citizenLprEvents',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_watch_lpr_events
                    ),
                },
                CitizenBlockReasons: {
                    path: 'citizen-block-reasons',
                    selectedItem: 'citizen-block-reasons',
                    component: CitizenBlockReasons,
                    label: 'citizenBlockReasons',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_management_banishment
                    ),
                    accessInformation: {
                        icon: faBan,
                        navigate: (navigation) => navigation.navigate('Citizen', { screen: 'CitizenBlockReasons' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
            }
        },
        WebRTC: {
            path: '/webrtc',
            stackNavigator: createStackNavigator<WebRtcParamList>(),
            canAccess: (user, currentApp) => Boolean(user?.isAdmin && currentApp.isParentApp),
            label: 'webrtc',
            icon: require('../assets/icon_webrtc.png'),
            logo: require('../assets/logo_webrtc.png'),
            navigate: (navigation) => navigation.navigate('WebRTC'),
            schemas: WebRtcSchemas,
            screens: {
                Publisher: {
                    path: 'publisher',
                    selectedItem: 'publisher',
                    component: Publisher,
                    label: 'publisher',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faCloudArrowUp,
                        navigate: (navigation) => navigation.navigate('WebRTC', { screen: 'Publisher' }),
                    },
                },
                Consumer: {
                    path: 'consumer',
                    selectedItem: 'consumer',
                    component: Consumer,
                    label: 'consumer',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faCloudArrowDown,
                        navigate: (navigation) => navigation.navigate('WebRTC', { screen: 'Consumer' }),
                    },
                },
            }
        },
        StreamServers: {
            path: '/stream-servers',
            stackNavigator: createStackNavigator<StreamServersParamList>(),
            canAccess: (user, currentApp) => Boolean(user?.isAdmin && currentApp.isParentApp),
            label: 'streamServers',
            icon: require('../assets/icon_stream_servers.png'),
            logo: require('../assets/logo_stream_servers.png'),
            navigate: (navigation) => navigation.navigate('StreamServers'),
            schemas: StreamServersSchemas,
            screens: {
                ServerFarms: {
                    path: 'server-farms',
                    selectedItem: 'server-farms',
                    component: ServerFarms,
                    label: 'serverFarms',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faServer,
                        navigate: (navigation) => navigation.navigate('StreamServers', { screen: 'ServerFarms' }),
                    },
                },
                ServerFarmDetail: {
                    path: 'server-farm/:id',
                    selectedItem: 'server-farms',
                    component: ServerFarmDetail,
                    label: 'serverFarmDetail',
                    canAccess: () => true,
                }
            }
        },
    },
};

export const modulePages: { [screen: string]: { modulePath: string; selectedItem: string; }; } = {};

const config: RouteConfig = { screens: {} };

for (const [screen, screenObj] of Object.entries(applicationPages.screens)) {

    const schema = RootSchemas[screen as keyof RootScreensParamLists];

    config.screens[screen] = {
        path: screenObj.path,
        parse: schema ? createLinkingParse(schema) : undefined,
    };

}

for (const [screen, screenObj] of Object.entries(applicationPages.modules)) {
    config.screens[screen] = {
        path: screenObj.path,
        screens: {},
    };

    for (const [moduleScreen, moduleScreenObj] of Object.entries(screenObj.screens)) {
        modulePages[moduleScreen] = { modulePath: screenObj.path, selectedItem: moduleScreenObj.selectedItem };

        const schema = screenObj.schemas[moduleScreen];

        config.screens[screen].screens![moduleScreen] = {
            path: moduleScreenObj.path,
            parse: createLinkingParse(schema),
        };
    }
}

export const linking = {
    prefixes: ['https://sentinelx.com.br'],
    config,
};


export function Loader() {
    const { theme } = useStyles();

    return <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size='large' color={theme.colors.loadingColor} />
    </View>;
}

export function MySafeAreaProvider(props: {
    children: React.ReactNode | undefined;
    navigationRef: NavigationContainerRefWithCurrent<RootStackParamList>;
}) {
    const { theme } = useStyles();
    const [, updateState] = React.useState({});
    const forceUpdate = React.useCallback(() => updateState({}), []);

    props.navigationRef.addListener('state', () => {
        forceUpdate();
    });

    const pathname = useMemo(() => window.location.pathname, [window.location.pathname]);

    return <SafeAreaProvider
        style={{
            backgroundColor: theme.colors.defaultPageContainer.background
        }}
    >
        <MenuProvider>
            <NavigationContainer ref={props.navigationRef} linking={linking} theme={{
                ...DefaultTheme,
                colors: {
                    ...DefaultTheme.colors,
                    background: theme.colors.defaultPageContainer.background
                }
            }} >
                {pathname.includes('/reset-password/') || pathname.includes('/login') ?
                    props.children
                    : <DefaultPageContainer >
                        <DefaultPageLayout navigationRef={props.navigationRef}>
                            {props.children}
                        </DefaultPageLayout>
                    </DefaultPageContainer>
                }
            </NavigationContainer>
        </MenuProvider>
    </SafeAreaProvider>;
}

function renderModule<K extends keyof ModuleParamLists>(
    module: ModuleConfig[K],
    authenticatedUser: AuthenticatedUser | null | undefined,
    currentApp: CurrentApp,
) {
    const { Navigator, Screen } = module.stackNavigator;

    return (
        <Navigator>
            {Object.entries(module.screens).map(([screenName, screen]) => {

                if (!(screen as ModulePage<keyof ModuleParamLists, never>).canAccess(authenticatedUser, currentApp)) {
                    return null;
                }

                return (
                    <Screen
                        key={screenName as string}
                        name={screenName as keyof ModuleParamLists[K]}
                        component={(screen as ModulePage<keyof ModuleParamLists, never>).component}
                        initialParams={(screen as ModulePage<keyof ModuleParamLists, never>).initialParams?.()}
                        options={{
                            headerShown: false,
                            title: translate((screen as ModulePage<keyof ModuleParamLists, never>).label),
                        }}
                    />
                );
            })}
        </Navigator>
    );
}

export default function MainRoutes() {

    const { user: authenticatedUser } = useAuth();
    const currentApp = useCurrentApp();

    return (
        <Stack.Navigator>

            {Object.entries(applicationPages.screens).map(([screenName, screenObj]) => {
                if (!screenObj.canAccess(authenticatedUser, currentApp)) {
                    null;
                }
                return <Stack.Screen key={screenName} name={screenName as keyof RootScreensParamLists} component={screenObj.component} options={{
                    headerShown: false,
                    title: translate(screenObj.label),
                }} />;
            })}

            {Object.entries(applicationPages.modules).map(([moduleName, moduleObj]) => {
                const canAccessModule = moduleObj.canAccess(authenticatedUser, currentApp) &&
                    Object.values(
                        moduleObj.screens,
                    ).some(screen => screen.canAccess(authenticatedUser, currentApp));

                if (!canAccessModule) {
                    return null;
                }

                return <Stack.Screen key={moduleName} name={moduleName as keyof ModuleParamLists} options={{ headerShown: false }}>
                    {() => renderModule(moduleObj, authenticatedUser, currentApp)}
                </Stack.Screen>;
            })}
        </Stack.Navigator >
    );
}
